import * as yup from "yup";
import type { SyncableEntityField } from "@hightouch/lib/external-context-sync/synced-fields-types";
import {
  AgentScope,
  type AgenticSettingsCopyExamplesContentInput,
  AgenticSettingsEntityType,
  ContextFileType,
  type CreateAgenticSettingsEntityInput,
  type UpdateAgenticSettingsEntityInput,
} from "src/graphql/types";
import {
  type CopyExamplesContent,
  type ContextEntity,
  type ContextEntityFormValues,
  isCopyExamplesEntity,
  isFileEntity,
  isMarkdownEntity,
} from "./types";
import { MIME_TYPE_TO_EXTENSION } from "@hightouch/lib/util/mime-types";

// Sentinel id for the explicit "All profiles" option in brand-profile
// selectors. The BE treats an empty assignment list as "applies to all".
export const ALL_PROFILES_ID = "__all_brand_profiles__";

// Human-readable labels for each context type. Covers every ContextFileType,
// including `skill` and `style_image` — users can't pick those in the entity
// editor (each has its own surface), but a proposal can still carry them, so
// anything rendering a classification needs a label for them.
export const CONTEXT_FILE_TYPE_LABELS: Record<ContextFileType, string> = {
  [ContextFileType.BrandGuidelines]: "Brand guidelines",
  [ContextFileType.BusinessContext]: "Business context",
  [ContextFileType.Skill]: "Skill",
  [ContextFileType.StyleImage]: "Style image",
  [ContextFileType.WinnerCriteria]: "Winner criteria",
  [ContextFileType.LaunchPlaybook]: "Launch playbook",
};

// Context types a user can assign in the entity editor. `skill` and
// `style_image` are excluded: each is created and managed on its own tab.
export const SELECTABLE_CONTEXT_FILE_TYPES: ContextFileType[] = [
  ContextFileType.BrandGuidelines,
  ContextFileType.BusinessContext,
];

// Human-readable labels for each agent scope. Only the scopes users can pick
// (see SELECTABLE_AGENT_SCOPES) are surfaced in the UI.
export const AGENT_SCOPE_LABELS: Record<AgentScope, string> = {
  [AgentScope.Ads]: "Ad content",
  [AgentScope.Lcm]: "Lifecycle content",
  [AgentScope.Data]: "Data",
};

// Helper text under a scope option in the scope selects.
export const AGENT_SCOPE_DESCRIPTIONS: Partial<Record<AgentScope, string>> = {
  [AgentScope.Lcm]: "Email, Push, SMS",
};

// Scopes a user can assign a context file to. An empty selection means the
// file applies to all agents, so there is no "All" sentinel option here.
export const SELECTABLE_AGENT_SCOPES: AgentScope[] = [
  AgentScope.Ads,
  AgentScope.Lcm,
  AgentScope.Data,
];

export const contextEntityFormSchema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Give this context a name")
    .notOneOf(["Untitled"], "Give this context a more descriptive name"),
  type: yup.string().required(),
  contextType: yup
    .string<string | null>()
    .oneOf([
      ContextFileType.BrandGuidelines,
      ContextFileType.BusinessContext,
      ContextFileType.Skill,
      null,
    ])
    .nullable(),
  markdown: yup.string().when("type", {
    is: AgenticSettingsEntityType.ContextMarkdown,
    then: (schema) =>
      schema.trim().required("Add some content or upload a file"),
    otherwise: (schema) => schema,
  }),
  existingFile: yup.mixed().nullable(),
  stagedFile: yup.mixed().nullable(),
  agentScopes: yup
    .array()
    .of(yup.string<AgentScope>().oneOf(SELECTABLE_AGENT_SCOPES).required())
    .default([]),
});

// Maps a fetched copy-examples content object to the mutation input. We explicitly
// map each field rather than spreading `content` because the query types carry
// `__typename` on every nested object and expose read-only fields absent from
// the input
export const copyExamplesContentToInput = (
  content: CopyExamplesContent,
): AgenticSettingsCopyExamplesContentInput => ({
  channel: content.channel,
  source: {
    kind: content.source.kind,
    fivetranSyncId: content.source.fivetranSyncId,
    resolvedSource: content.source.resolvedSource
      ? {
          table: content.source.resolvedSource.table,
          columns: {
            id: content.source.resolvedSource.columns.id,
            name: content.source.resolvedSource.columns.name,
            subject: content.source.resolvedSource.columns.subject,
            preheader: content.source.resolvedSource.columns.preheader,
            body: content.source.resolvedSource.columns.body,
            updatedAt: content.source.resolvedSource.columns.updatedAt,
            isDraft: content.source.resolvedSource.columns.isDraft,
            isArchived: content.source.resolvedSource.columns.isArchived,
          },
        }
      : null,
  },
  extractionParams: {
    lookbackDays: content.extractionParams.lookbackDays,
    minSends: content.extractionParams.minSends,
    targetCount: content.extractionParams.targetCount,
    rowFilterNote: content.extractionParams.rowFilterNote,
  },
  examples: content.examples.map((example) => ({
    id: example.id,
    sourceId: example.sourceId,
    subject: example.subject,
    preheader: example.preheader,
    title: example.title,
    cta: example.cta,
    body: example.body,
    rawBody: example.rawBody,
    campaignName: example.campaignName,
    sentAt: example.sentAt,
    excluded: example.excluded,
    exclusionReason: example.exclusionReason,
    imageUrls: example.imageUrls,
  })),
});

/**
 * `uploadedS3Key` is the storage key returned by useUploadContextFile for the
 * staged file — the file bytes are PUT to a presigned URL before the mutation
 * runs, and the mutation only carries the key.
 */
export const buildCreateInput = (
  values: ContextEntityFormValues,
  uploadedS3Key: string | null,
): CreateAgenticSettingsEntityInput => {
  const base = {
    name: values.name.trim(),
    description: values.description.trim() ? values.description.trim() : null,
    isActive: values.isActive,
    // References carry no classification or agent routing — the form hides
    // those fields, so clear them structurally rather than persisting whatever
    // the form last held (a later flip back must not restore stale values).
    contextType: values.isReferenceOnly ? null : values.contextType,
    agentScopes: values.isReferenceOnly ? [] : values.agentScopes,
    brandProfileIds: values.brandProfileIds,
    isReferenceOnly: values.isReferenceOnly,
  };
  if (values.type === AgenticSettingsEntityType.ContextMarkdown) {
    return {
      ...base,
      type: AgenticSettingsEntityType.ContextMarkdown,
      contextMarkdown: { markdown: values.markdown },
    };
  }
  if (!values.stagedFile || !uploadedS3Key) {
    throw new Error("A file is required to create a context_file entity");
  }
  return {
    ...base,
    type: AgenticSettingsEntityType.ContextFile,
    contextFile: {
      file: {
        filename: values.stagedFile.filename,
        filetype: values.stagedFile.filetype,
        s3Key: uploadedS3Key,
      },
    },
  };
};

/**
 * Build an `UpdateAgenticSettingsEntityInput` that toggles `isActive` while
 * preserving every other field. Content is never sent: file entities pass
 * `file: null` and markdown entities omit `contextMarkdown`, so the backend
 * keeps the latest stored content rather than this cached copy.
 */
export const buildSetActiveInput = (
  entity: ContextEntity,
  isActive: boolean,
): UpdateAgenticSettingsEntityInput => {
  const base = {
    id: entity.id,
    name: entity.name,
    description: entity.description ?? null,
    isActive,
    // Preserve the classification when only toggling active state (the backend
    // also leaves an omitted contextType untouched, but forward it explicitly).
    contextType: entity.contextType,
    // Forward the agent scope so toggling active state doesn't clobber it.
    agentScopes: entity.agentScopes,
  };
  if (isMarkdownEntity(entity)) {
    return { ...base, type: AgenticSettingsEntityType.ContextMarkdown };
  }
  if (isFileEntity(entity)) {
    return {
      ...base,
      type: AgenticSettingsEntityType.ContextFile,
      contextFile: { file: null },
    };
  }
  if (isCopyExamplesEntity(entity)) {
    return {
      ...base,
      type: AgenticSettingsEntityType.CopyReferences,
      contextCopyExamples: copyExamplesContentToInput(entity.content),
    };
  }
  throw new Error("Unknown agentic settings entity type");
};

type BuildUpdateInputArgs = {
  id: string;
  values: ContextEntityFormValues;
  /** Storage key from useUploadContextFile for a staged replacement file, or null. */
  uploadedS3Key: string | null;
  syncedFields: SyncableEntityField[];
};

export const buildUpdateInput = ({
  id,
  values,
  uploadedS3Key,
  syncedFields,
}: BuildUpdateInputArgs): UpdateAgenticSettingsEntityInput => {
  const base = {
    id,
    name: values.name.trim(),
    description: values.description.trim() ? values.description.trim() : null,
    isActive: values.isActive,
    // See buildCreateInput: a reference must not keep (or later restore) a
    // stale classification/agent scope from before the flip.
    contextType: values.isReferenceOnly ? null : values.contextType,
    agentScopes: values.isReferenceOnly ? [] : values.agentScopes,
    isReferenceOnly: values.isReferenceOnly,
  };
  if (values.type === AgenticSettingsEntityType.ContextMarkdown) {
    // Synced content is read-only, and the editor's markdown round-trip isn't
    // byte-stable, so omit it and let the backend keep the stored content.
    if (syncedFields.includes("content")) {
      return { ...base, type: AgenticSettingsEntityType.ContextMarkdown };
    }
    return {
      ...base,
      type: AgenticSettingsEntityType.ContextMarkdown,
      contextMarkdown: { markdown: values.markdown },
    };
  }
  // For file entities: pass the new file payload only when the user staged a
  // replacement. Otherwise pass `file: null` so the backend reuses the prior
  // version's S3 key (saves an upload and avoids changing the storage key).
  return {
    ...base,
    type: AgenticSettingsEntityType.ContextFile,
    contextFile: {
      file:
        values.stagedFile && uploadedS3Key
          ? {
              filename: values.stagedFile.filename,
              filetype: values.stagedFile.filetype,
              s3Key: uploadedS3Key,
            }
          : null,
    },
  };
};

export const formValuesFromEntity = (
  entity: ContextEntity,
  brandProfileIds: string[] = [],
): ContextEntityFormValues => {
  const base = {
    name: entity.name,
    description: entity.description ?? "",
    isActive: entity.isActive,
    isReferenceOnly: entity.isReferenceOnly,
    contextType: entity.contextType ?? null,
    agentScopes: entity.agentScopes ?? [],
    stagedFile: null,
    brandProfileIds,
  };
  if (isMarkdownEntity(entity)) {
    return {
      ...base,
      type: AgenticSettingsEntityType.ContextMarkdown,
      markdown: entity.content.markdown,
      existingFile: null,
    };
  }
  if (isFileEntity(entity)) {
    return {
      ...base,
      type: AgenticSettingsEntityType.ContextFile,
      markdown: "",
      existingFile: {
        filename: entity.content.filename,
        filetype: entity.content.filetype,
        filesize: entity.content.filesize,
        url: entity.content.url,
      },
    };
  }
  throw new Error("Unknown agentic settings entity type");
};

// `contextType` seeds the classification for a new entry — the Skills surface
// passes ContextFileType.Skill so drafts are created as skills; the general
// Context tab omits it (unclassified).
export const emptyFormValues = (
  contextType: ContextFileType | null = null,
): ContextEntityFormValues => ({
  name: "Untitled",
  description: "",
  isActive: true,
  isReferenceOnly: false,
  contextType,
  agentScopes: [],
  type: AgenticSettingsEntityType.ContextMarkdown,
  markdown: "",
  existingFile: null,
  stagedFile: null,
  brandProfileIds: [],
});

export const ORPHANED_ENTITY_WARNING =
  "Assigned brand profiles have been archived. Context won't be used until you assign it to a brand profile.";

// An entity is "orphaned" when every assigned profile has been soft-deleted.
export const isOrphanedEntity = (entity: ContextEntity): boolean =>
  entity.brandProfileAssignments.length > 0 &&
  entity.brandProfileAssignments.every((a) => a.isDeleted);

// Initial form value for the brand-profile MultiSelect — ids of the live
// (non-deleted) profiles this entity is currently assigned to.
export const liveBrandProfileIds = (
  entity: Pick<ContextEntity, "brandProfileAssignments">,
): string[] =>
  entity.brandProfileAssignments.filter((a) => !a.isDeleted).map((a) => a.id);

// Names of the live (non-deleted) profiles this entity is assigned to, for
// display in the context table.
export const liveBrandProfileNames = (entity: ContextEntity): string[] =>
  entity.brandProfileAssignments.filter((a) => !a.isDeleted).map((a) => a.name);

// An entity with zero assignments applies to all brand profiles by convention.
export const appliesToAllBrandProfiles = (entity: ContextEntity): boolean =>
  entity.brandProfileAssignments.length === 0;

export type PublishedStatus = "published" | "draft";

export const PUBLISHED_STATUS_LABELS: Record<PublishedStatus, string> = {
  published: "Published",
  draft: "Draft",
};

export const filterByPublishedStatus = (
  entities: ContextEntity[],
  statuses: PublishedStatus[],
): ContextEntity[] =>
  entities.filter((entity) =>
    statuses.includes(isEffectivelyActive(entity) ? "published" : "draft"),
  );

/**
 * Mirrors the backend read filter (`appliesToFilter` in
 * `packages/backend/lib/brand-profiles/assignments.ts`): an entity with zero
 * *live* assignments applies to every profile, so an orphaned entity matches
 * too. Otherwise it matches when assigned to any selected profile. A `null`
 * selection is unfiltered, and an empty one matches nothing.
 */
export const filterByBrandProfiles = <
  T extends Pick<ContextEntity, "brandProfileAssignments">,
>(
  entities: T[],
  brandProfileIds: string[] | null,
): T[] => {
  if (brandProfileIds === null) return entities;
  if (brandProfileIds.length === 0) return [];
  return entities.filter((entity) => {
    const liveIds = liveBrandProfileIds(entity);
    return (
      liveIds.length === 0 || liveIds.some((id) => brandProfileIds.includes(id))
    );
  });
};

/**
 * An entity with no agent scopes applies to every agent. A `null` selection is
 * unfiltered, and an empty one matches nothing.
 */
export const filterByAgentScopes = <
  T extends { agentScopes?: AgentScope[] | null },
>(
  entities: T[],
  agentScopes: AgentScope[] | null,
): T[] => {
  if (agentScopes === null) return entities;
  if (agentScopes.length === 0) return [];
  return entities.filter(
    (entity) =>
      !entity.agentScopes?.length ||
      entity.agentScopes.some((scope) => agentScopes.includes(scope)),
  );
};

export const filterBySearch = (
  entities: ContextEntity[],
  search: string,
): ContextEntity[] => {
  const q = search.trim().toLowerCase();
  if (!q) return entities;
  return entities.filter(
    (entity) =>
      entity.name.toLowerCase().includes(q) ||
      entity.description?.toLowerCase().includes(q) ||
      (isFileEntity(entity) &&
        entity.content.filename.toLowerCase().includes(q)),
  );
};

/** A managed skill's workspace kill switch can override its global default. */
export const isEffectivelyActive = (entity: ContextEntity): boolean =>
  entity.isActive && !entity.workspaceDisabled && !isOrphanedEntity(entity);

export const contentPreview = (entity: ContextEntity): string => {
  if (isMarkdownEntity(entity)) {
    return entity.content.markdown.replace(/\s+/g, " ").trim();
  }
  if (isFileEntity(entity)) {
    return entity.content.filename;
  }
  if (isCopyExamplesEntity(entity)) {
    const includedCount = entity.content.examples.filter(
      (example) => !example.excluded,
    ).length;
    return `${includedCount} of ${entity.content.examples.length} ${entity.content.channel} examples included`;
  }
  return "";
};

// Return extension from filename if it exists, else derive from filetype (MIME type).
// Fall back to "file"
export const fileTypeBadge = (filetype: string, filename: string): string => {
  const extension = filename.includes(".") ? filename.split(".").pop() : null;
  return extension || MIME_TYPE_TO_EXTENSION[filetype] || "file";
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Sentinel used to represent the unclassified choice in the Select, which maps
// to a null `contextType`.
export const CONTEXT_TYPE_NONE = "none";

type ContextTypeOption = { value: string; label: string; description?: string };

// An unclassified file is read on demand; the classified types are brand
// context, which agents load into every chat.
export const CONTEXT_TYPE_OPTIONS: ContextTypeOption[] = [
  {
    value: CONTEXT_TYPE_NONE,
    label: "When relevant",
    description: "The agent reads this file when it's useful for the task.",
  },
  ...SELECTABLE_CONTEXT_FILE_TYPES.map((type) => ({
    value: type,
    label: `Every chat (${CONTEXT_FILE_TYPE_LABELS[type].toLowerCase()})`,
    description:
      "The agent reads this file in every chat. Reserve this for essential context that applies to nearly every task.",
  })),
];

/** A launch playbook, offered only to users who can launch through the agent. */
export const LAUNCH_PLAYBOOK_CONTEXT_TYPE_OPTION: ContextTypeOption = {
  value: ContextFileType.LaunchPlaybook,
  label: CONTEXT_FILE_TYPE_LABELS[ContextFileType.LaunchPlaybook],
  description:
    "The agent reads this file when it launches to the platform the file names.",
};
